import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GAME_RESPONSES, TRIVIA_RESPONSE_REPORT } from "../../../../graphql/queries/ReportQueries";
import { SelectInput } from "../../../shared/ui/SelectInput";
import { WithQueryLoader } from "../../../shared/WithQueryLoader";
import { GameReportProps } from "../../GameReport/GameReport";
import { SelectOption } from "../../../shared/ui/SelectInput/SelectInput";
import { Section } from "../../../project/ProjectView/ProjectSettings/Steps/Common";
import { Chart, TriviaChartData } from "../Chart/Chart";

import styles from "../TriviaChart.module.scss";
import headerStyles from "../../ChartHeader/ChartHeader.module.scss";
import { Paragraph } from "@datagame/ui";

export const Detailed = ({ game }: GameReportProps) => {
  const [response, setResponse] = useState<SelectOption>({
    label: "There are no responses yet",
    value: "default",
  });
  const { t } = useTranslation();

  const { data, loading } = useQuery(TRIVIA_RESPONSE_REPORT, {
    variables: {
      responseId: response?.value || "",
      gameId: game.id,
    },
    skip: response.value === "default",
  });

  const chartData = useMemo((): TriviaChartData[] => {
    if (!data) return [];

    return data.triviaResponseReport.scores.map((score) => {
      return {
        id: score.question.id,
        question: score.question.question,
        options: score.question.options.map((option) => {
          return {
            text: option.text,
            count: option.id === score.answer.id ? 1 : 0,
          };
        }),
      };
    });
  }, [data]);

  const stats = useMemo(() => {
    if (!data) {
      return {
        ratio: 0,
        correct: 0,
        total: 0,
      };
    }

    const { numberOfwrongAnswers, numberOfCorrectAnswers } = data.triviaResponseReport;

    const total = numberOfwrongAnswers + numberOfCorrectAnswers;
    const ratio = total === 0 ? 0 : (numberOfCorrectAnswers / total) * 100;
    const correct = numberOfCorrectAnswers;

    return {
      ratio,
      correct,
      total,
    };
  }, [data]);

  return (
    <>
      <Section title={t("report:selectResponse")} className={styles.section}>
        <ResponseSelect gameId={game.id} setResponse={setResponse} response={response} />
      </Section>

      {response && (
        <WithQueryLoader isLoading={loading}>
          <>
            <div className={styles.container}>
              <div className={headerStyles.chartHeader}>
                <Paragraph weight="font-bold" color="secondary">
                  {t("report:trivia.header.detailed")}
                </Paragraph>
                <Paragraph size="small" color="secondary">
                  {t("report:trivia.correctResponsesRatio", {
                    correct: stats.correct,
                    ratio: stats.ratio,
                    total: stats.total,
                  })}
                </Paragraph>
              </div>
            </div>

            {chartData.map((row) => (
              <Chart key={row.id} data={row} />
            ))}
          </>
        </WithQueryLoader>
      )}
    </>
  );
};

const ResponseSelect = ({
  gameId,
  setResponse,
  response,
}: {
  gameId: string;
  setResponse: (params: SelectOption) => void;
  response?: SelectOption;
}) => {
  const { data, loading } = useQuery(GAME_RESPONSES, {
    variables: {
      gameId,
    },
    onCompleted: (data) => {
      if (data.game.responses.length > 0) {
        const id = data.game.responses[0].id;
        setResponse({
          label: id,
          value: id,
        });
      }
    },
  });

  const responseOptions =
    data?.game.responses.map((response) => ({
      label: response.id,
      value: response.id,
    })) || [];

  return (
    <WithQueryLoader isLoading={loading}>
      <SelectInput options={responseOptions} selected={response} onChange={setResponse} />
    </WithQueryLoader>
  );
};
